import React, { useState, useEffect } from 'react';
import {
  updateEnvironment,
  getApprovers,
  addApprover,
  removeApprover,
  type EnvironmentApprover,
} from '../../api/environments';
import { listUsers, type OrgUser } from '../../api/users';

interface Props {
  envId: string;
  envName: string;
  requiresApproval: boolean;
  autoApprove: boolean;
  approvalRequiredCount: number;
  onClose: () => void;
  onSaved: () => void;
}

const EnvironmentApproversModal: React.FC<Props> = ({
  envId,
  envName,
  requiresApproval: initialRequiresApproval,
  autoApprove: initialAutoApprove,
  approvalRequiredCount: initialCount,
  onClose,
  onSaved,
}) => {
  const [approvers, setApprovers] = useState<EnvironmentApprover[]>([]);
  const [orgUsers, setOrgUsers] = useState<OrgUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [requiresApproval, setRequiresApproval] = useState(initialRequiresApproval);
  const [autoApprove, setAutoApprove] = useState(initialAutoApprove);
  const [approvalRequiredCount, setApprovalRequiredCount] = useState(initialCount || 1);

  const [selectedUserId, setSelectedUserId] = useState('');
  const [addingApprover, setAddingApprover] = useState(false);

  useEffect(() => {
    loadData();
  }, [envId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [approversList, users] = await Promise.all([
        getApprovers(envId),
        listUsers(),
      ]);
      setApprovers(approversList);
      setOrgUsers(users);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const showSuccess = (msg: string) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(null), 2500);
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      setError(null);
      await updateEnvironment(envId, { requiresApproval, autoApprove, approvalRequiredCount });
      onSaved();
      showSuccess('Paramètres sauvegardés');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleAddApprover = async () => {
    if (!selectedUserId) return;
    try {
      setAddingApprover(true);
      setError(null);
      await addApprover(envId, selectedUserId);
      setSelectedUserId('');
      await loadData();
      showSuccess('Approbateur ajouté');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur lors de l\'ajout');
    } finally {
      setAddingApprover(false);
    }
  };

  const handleRemoveApprover = async (userId: string, userName?: string) => {
    if (!window.confirm(`Retirer ${userName || 'cet approbateur'} ?`)) return;
    try {
      setError(null);
      await removeApprover(envId, userId);
      await loadData();
      showSuccess('Approbateur retiré');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  const availableUsers = orgUsers.filter(
    (u) => !approvers.some((a) => a.userId === u.id)
  );

  const getRoleBadge = (role: string) => {
    const map: Record<string, string> = {
      owner: 'bg-purple-100 text-purple-800',
      admin: 'bg-blue-100 text-blue-800',
      member: 'bg-gray-100 text-gray-700',
      viewer: 'bg-green-100 text-green-700',
    };
    return map[role] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto text-gray-900">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Approbations</h2>
            <p className="text-sm text-gray-500">{envName}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition text-xl leading-none">✕</button>
        </div>

        <div className="p-6 space-y-6">
          {/* Notifications */}
          {error && (
            <div className="bg-red-50 border border-red-300 rounded-lg p-3 text-red-800 text-sm">⚠️ {error}</div>
          )}
          {success && (
            <div className="bg-green-50 border border-green-300 rounded-lg p-3 text-green-800 text-sm">✅ {success}</div>
          )}

          {/* Politique */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Politique d'approbation</h3>
            <div className="space-y-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={requiresApproval}
                  onChange={(e) => { setRequiresApproval(e.target.checked); if (!e.target.checked) setAutoApprove(false); }}
                  className="mt-0.5"
                />
                <div>
                  <div className="text-sm font-medium text-gray-800">Approbation requise</div>
                  <div className="text-xs text-gray-500">Les apply et destroy nécessitent une approbation avant de démarrer</div>
                </div>
              </label>

              {requiresApproval && (
                <label className="flex items-start gap-3 cursor-pointer ml-5">
                  <input
                    type="checkbox"
                    checked={autoApprove}
                    onChange={(e) => setAutoApprove(e.target.checked)}
                    className="mt-0.5"
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-800">Auto-approbation</div>
                    <div className="text-xs text-gray-500">Approuve automatiquement sans intervention manuelle</div>
                  </div>
                </label>
              )}

              {requiresApproval && !autoApprove && (
                <div className="ml-5 flex items-center gap-3">
                  <label className="text-sm text-gray-700">Approbations requises :</label>
                  <input
                    type="number"
                    min={1}
                    max={10}
                    value={approvalRequiredCount}
                    onChange={(e) => setApprovalRequiredCount(Math.max(1, parseInt(e.target.value) || 1))}
                    className="w-16 px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}
            </div>

            <button
              onClick={handleSaveSettings}
              disabled={saving}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition disabled:opacity-50"
            >
              {saving ? 'Sauvegarde...' : 'Sauvegarder'}
            </button>
          </div>

          {/* Approbateurs */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Approbateurs</h3>

            {/* Ajouter */}
            <div className="flex gap-2 mb-4">
              <select
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">— Sélectionner un utilisateur —</option>
                {availableUsers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.fullName} · {u.email}
                  </option>
                ))}
              </select>
              <button
                onClick={handleAddApprover}
                disabled={!selectedUserId || addingApprover}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition disabled:opacity-40"
              >
                {addingApprover ? '...' : '+ Ajouter'}
              </button>
            </div>

            {/* Liste */}
            {loading ? (
              <div className="text-center text-sm text-gray-400 py-4">Chargement...</div>
            ) : approvers.length === 0 ? (
              <div className="text-center py-8 border border-dashed border-gray-200 rounded-lg">
                <div className="text-2xl mb-1">👤</div>
                <div className="text-sm text-gray-400">Aucun approbateur</div>
                {requiresApproval && !autoApprove && (
                  <div className="text-xs text-amber-600 mt-1">⚠️ Requis mais aucun configuré !</div>
                )}
              </div>
            ) : (
              <div className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden">
                {approvers.map((approver, idx) => {
                  const user = orgUsers.find((u) => u.id === approver.userId);
                  return (
                    <div key={approver.userId} className="flex items-center justify-between px-4 py-3 hover:bg-gray-50">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-semibold">
                          {(approver.userName || user?.fullName || '?')[0].toUpperCase()}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {approver.userName || user?.fullName || approver.userId}
                          </div>
                          <div className="text-xs text-gray-500">{approver.userEmail || user?.email}</div>
                        </div>
                        {user && (
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getRoleBadge(user.role)}`}>
                            {user.role}
                          </span>
                        )}
                        {approvalRequiredCount > 1 && (
                          <span className="text-xs text-gray-400">#{idx + 1}</span>
                        )}
                      </div>
                      <button
                        onClick={() => handleRemoveApprover(approver.userId, approver.userName || user?.fullName)}
                        className="text-red-400 hover:text-red-600 text-sm px-2 py-1 rounded hover:bg-red-50 transition"
                      >
                        ✕
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {requiresApproval && !autoApprove && approvers.length > 0 && (
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800">
                {approvers.length >= approvalRequiredCount
                  ? `✅ ${approvers.length} approbateur${approvers.length > 1 ? 's' : ''} · ${approvalRequiredCount} requis pour valider`
                  : `⚠️ ${approvers.length}/${approvalRequiredCount} configurés — il en manque ${approvalRequiredCount - approvers.length}`
                }
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnvironmentApproversModal;
